import React, { useEffect } from 'react';
import { Outlet, Link, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store';
import {
  toggleSidebar,
  setSidebarOpen,
  setSelectedLeague,
  fetchBannerLeagues,
} from '../store/slices/uiSlice';
import { UserRole } from '@sulis-liiga/common';

const MainLayout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { sidebarOpen, bannerLeagues, bannerLoading, selectedLeagueId } = useSelector(
    (state: RootState) => state.ui,
  );

  // Load leagues for the banner on mount (public, no auth required)
  useEffect(() => {
    dispatch(fetchBannerLeagues());
  }, [dispatch]);

  // Close sidebar when clicking the mobile scrim
  const handleScrimClick = () => {
    dispatch(setSidebarOpen(false));
  };

  const isAdmin =
    user?.roles?.includes(UserRole.LEAGUE_ADMINISTRATOR) ||
    user?.roles?.includes(UserRole.SUPER_ADMINISTRATOR);

  const isSuperAdmin = user?.roles?.includes(UserRole.SUPER_ADMINISTRATOR);

  const initials = user ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}` : '';

  const selectedLeague =
    bannerLeagues.find(l => l.leagueId === selectedLeagueId) ?? bannerLeagues[0];

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-base font-medium transition-colors ${
      isActive
        ? 'text-primary-600 bg-primary-50'
        : 'text-gray-600 hover:bg-primary-50 hover:text-primary-600'
    }`;

  const SidebarContent = () => (
    <nav className="mt-5 px-2 space-y-1" aria-label="Main navigation">
      <NavLink to="/" end className={navLinkClass} aria-current="page">
        Home
      </NavLink>
      <NavLink
        to={selectedLeague ? `/leagues/${selectedLeague.leagueId}/divisions` : '/divisions'}
        className={navLinkClass}
      >
        Divisions
      </NavLink>
      <NavLink
        to={selectedLeague ? `/leagues/${selectedLeague.leagueId}/teams` : '/teams'}
        className={navLinkClass}
      >
        Teams
      </NavLink>
      <NavLink
        to={selectedLeague ? `/leagues/${selectedLeague.leagueId}/games` : '/games'}
        className={navLinkClass}
      >
        Games
      </NavLink>
      <NavLink to="/how-it-works" className={navLinkClass}>
        How it Works
      </NavLink>

      {isAuthenticated && isAdmin && (
        <>
          <div
            className="mt-4 px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide"
            aria-hidden="true"
          >
            Admin
          </div>
          <NavLink to="/leagues" className={navLinkClass}>
            Leagues
          </NavLink>
          {selectedLeague && (
            <NavLink to={`/leagues/${selectedLeague.leagueId}/players`} className={navLinkClass}>
              Players
            </NavLink>
          )}
        </>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <header className="bg-white shadow-sm sticky top-0 z-20 h-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            {/* Left: hamburger + wordmark */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="md:hidden text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                aria-label="Open menu"
                onClick={() => dispatch(toggleSidebar())}
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
              <Link to="/" className="text-xl font-bold text-primary-600">
                Sulis-Liiga
              </Link>
            </div>

            {/* Right: auth state */}
            {isAuthenticated && user ? (
              <Link
                to="/profile"
                className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-gray-50 transition-colors"
                aria-label={`${user.firstName} ${user.lastName} — profile`}
              >
                <span className="h-8 w-8 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-sm font-semibold select-none">
                  {initials}
                </span>
                <span className="text-sm font-medium hidden sm:block">
                  {user.firstName} {user.lastName}
                </span>
              </Link>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/register"
                  className="px-4 py-2 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-50 transition-colors"
                >
                  Sign up
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  Log in
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── League banner ────────────────────────────────────────────────── */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop: pill list */}
          <nav
            className="hidden md:flex items-center gap-2 h-12 overflow-x-auto"
            aria-label="Leagues"
          >
            <span className="text-xs uppercase tracking-wide text-gray-400 mr-2 shrink-0">
              League
            </span>
            {bannerLoading && <span className="text-sm text-gray-400 animate-pulse">Loading…</span>}
            {!bannerLoading &&
              bannerLeagues.map(league => (
                <button
                  key={league.leagueId}
                  type="button"
                  onClick={() =>
                    dispatch(
                      setSelectedLeague({
                        leagueId: league.leagueId,
                        tenantSlug: league.tenantSlug,
                      }),
                    )
                  }
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors shrink-0 ${
                    league.leagueId === selectedLeagueId
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-primary-50'
                  }`}
                  aria-current={league.leagueId === selectedLeagueId ? 'true' : undefined}
                >
                  {league.name}
                </button>
              ))}
            {!bannerLoading && bannerLeagues.length === 0 && (
              <span className="text-sm text-gray-400">No leagues available</span>
            )}
            {isSuperAdmin && (
              <Link
                to="/leagues/create"
                className="ml-2 px-3 py-1.5 rounded-full text-sm font-medium text-primary-600 border border-dashed border-primary-400 hover:bg-primary-50 transition-colors shrink-0"
              >
                + New league
              </Link>
            )}
          </nav>

          {/* Mobile: select dropdown */}
          <div className="flex md:hidden items-center h-12">
            <label
              htmlFor="league-select"
              className="text-xs uppercase tracking-wide text-gray-400 mr-2 shrink-0"
            >
              League
            </label>
            <select
              id="league-select"
              value={selectedLeagueId ?? ''}
              onChange={e => {
                const chosen = bannerLeagues.find(l => l.leagueId === e.target.value);
                if (chosen) {
                  dispatch(
                    setSelectedLeague({ leagueId: chosen.leagueId, tenantSlug: chosen.tenantSlug }),
                  );
                }
              }}
              className="flex-1 text-sm border-0 bg-transparent text-gray-700 focus:ring-0 focus:outline-none"
              aria-label="Select league"
            >
              {bannerLeagues.map(league => (
                <option key={league.leagueId} value={league.leagueId}>
                  {league.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Body: sidebar + content ──────────────────────────────────────── */}
      <div className="flex max-w-7xl mx-auto">
        {/* Mobile scrim */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black bg-opacity-50 md:hidden"
            aria-hidden="true"
            onClick={handleScrimClick}
          />
        )}

        {/* Sidebar (desktop pinned, mobile drawer) */}
        <aside
          className={`${
            sidebarOpen
              ? 'fixed inset-y-0 left-0 z-40 flex flex-col w-64'
              : 'hidden md:flex md:flex-col'
          } w-64 shrink-0 bg-white shadow-md min-h-[calc(100vh-7rem)]`}
        >
          {/* Mobile close button */}
          {sidebarOpen && (
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 md:hidden">
              <span className="text-lg font-bold text-primary-600">Menu</span>
              <button
                type="button"
                className="text-gray-500"
                aria-label="Close menu"
                onClick={() => dispatch(setSidebarOpen(false))}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}
          <SidebarContent />
        </aside>

        {/* Main content */}
        <main className="flex-1 md:ml-0 p-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
